import React from 'react';
import { Box, Typography, Paper, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../common/contexts/ThemeContext';

/**
 * Dashboard Component (Refreshed)
 *
 * Replaces previous content with a central hub visualization:
 * a central circle (Healthcare System) surrounded by six satellite circles
 * matching the Iris homepage styling (black background, gradient headers,
 * cyan accents, subtle hover elevation/scale).
 */
const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  // Shared gradient text style consistent with homepage
  const gradientText = {
    background: 'linear-gradient(90deg, #8A2BE2, #00B4FF)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent'
  } as const;

  // Circle button factory to keep styles consistent
  const CircleButton: React.FC<{
    label: string;
    onClick?: () => void;
    disabled?: boolean;
    badgeCount?: number;
  }> = ({ label, onClick, disabled, badgeCount }) => {
    const isAlerts = label === 'Iris Alerts';
    return (
      <Paper
        onClick={disabled ? undefined : onClick}
        role="button"
        elevation={3}
        sx={{
          position: 'relative',
          width: { xs: 140, sm: 160, md: 180 },
          height: { xs: 140, sm: 160, md: 180 },
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: 2,
          cursor: disabled ? 'default' : 'pointer',
          backgroundColor: theme.palette.background.paper,
          border: isAlerts ? '1px solid rgba(255,0,0,0.45)' : isDark ? '1px solid rgba(138,43,226,0.35)' : '1px solid rgba(138,43,226,0.25)',
          boxShadow: isAlerts ? '0 0 25px rgba(255,0,0,0.35)' : undefined,
          transition: 'transform 0.25s ease, box-shadow 0.25s ease',
          '&:hover': disabled
            ? {}
            : {
                transform: 'translateY(-2px) scale(1.03)',
                boxShadow: isAlerts
                  ? '0 0 35px rgba(255,0,0,0.45)'
                  : '0 10px 25px rgba(0, 180, 255, 0.25)'
              }
        }}
      >
        {typeof badgeCount === 'number' && badgeCount > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 10,
              width: 18,
              height: 18,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,0,0,0.9)',
              color: '#fff',
              fontSize: '0.7rem',
              fontWeight: 800,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 12px rgba(255,0,0,0.6)'
            }}
          >
            {badgeCount}
          </Box>
        )}
        <Typography
          variant="body1"
          sx={{
            ...gradientText,
            fontWeight: 700,
            lineHeight: 1.2,
            fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.15rem' }
          }}
        >
          {label}
        </Typography>
      </Paper>
    );
  };

  // Determine container sizes based on breakpoints to compute equal spacing
  const isMdUp = useMediaQuery('(min-width:900px)');
  const isSmUp = useMediaQuery('(min-width:600px)');
  const containerSize = isMdUp ? 750 : isSmUp ? 560 : 480;
  const center = containerSize / 2;
  const satelliteSize = isMdUp ? 180 : isSmUp ? 160 : 140;
  const centralSize = isMdUp ? 280 : isSmUp ? 230 : 200;
  // Radius positions satellites around the central circle with an intentional gap
  const gapBoost = isMdUp ? 24 : isSmUp ? 20 : 17; // extra pixels to prevent touching
  const radius = (containerSize - centralSize) / 2 - satelliteSize * 0.1 + gapBoost;

  const satellites: Array<{ label: string; onClick?: () => void } > = [
    { label: 'Iris Care Score', onClick: () => navigate('/care-score') },
    { label: 'Top Facilities', onClick: () => navigate('/map') },
    { label: 'Top Providers' },
    { label: 'Map', onClick: () => navigate('/map') }, // index 3 => bottom
    { label: 'Top Patient Cohorts', onClick: () => navigate('/patient-cohorts') },
    { label: 'Iris Alerts' }
  ];

  // Hover state for contextual panels - modals auto-close when cursor leaves
  const [hovered, setHovered] = React.useState<string | null>(null);
  const closeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Helper to set hover with delay cancellation
  const handleHoverEnter = (label: string) => {
    // Cancel any pending close
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setHovered(label);
  };

  // Helper to close with small delay to allow cursor movement
  const handleHoverLeave = () => {
    // Small delay before closing to allow cursor to move between circle and modal
    closeTimeoutRef.current = setTimeout(() => {
      setHovered(null);
      closeTimeoutRef.current = null;
    }, 150); // 150ms delay
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const facilitiesItems = [
    { id: 'FL123', name: 'Main Street Primary Care', metric: '$4.5M PSR, T12M' },
    { id: 'FL124', name: 'Memorial Hospital West', metric: '$650M PSR, T12M' },
    { id: 'FL125', name: 'Oak Hill Surgery Center', metric: '$45M PSR, T12M' },
    { id: 'FL126', name: 'Coastal Cardiology Center', metric: '$22M PSR, T12M' },
    { id: 'FL127', name: 'Cedar Pediatric Group', metric: '$12M PSR, T12M' },
  ];

  // Sort facilities by monetary amount in metric (descending)
  const parseAmount = (metric: string): number => {
    const match = metric.match(/\$([0-9]*\.?[0-9]+)\s*([MB])?/i);
    if (!match) return 0;
    const value = parseFloat(match[1]);
    const unit = (match[2] || 'M').toUpperCase(); // default to M if missing
    const multiplier = unit === 'B' ? 1_000 : 1; // express everything in millions
    return value * multiplier; // number in millions
  };

  const facilitiesSorted = [...facilitiesItems].sort((a, b) => parseAmount(b.metric) - parseAmount(a.metric));

  // Patient cohorts hover data and sorting by unique patients (descending)
  const cohortsItems = [
    { id: 'PC001', name: 'High-risk Diabetics', metric: '6K unique patients, T12M' },
    { id: 'PC002', name: 'Ortho Referrals: Hip/Knee', metric: '1.8K unique patients, T12M' },
    { id: 'PC003', name: 'New Mom & Pediatric Wellness', metric: '9.5K unique patients, T12M' },
    { id: 'PC004', name: 'High ED Utilization: Low Acuity', metric: '4K unique patients, T12M' },
    { id: 'PC005', name: 'Chronic COPD: Readmission Risk', metric: '4.5K unique patients, T12M' },
  ];

  const parsePatients = (metric: string): number => {
    const match = metric.match(/([0-9]*\.?[0-9]+)\s*K/i);
    if (!match) return 0;
    const value = parseFloat(match[1]);
    return value * 1000;
  };

  const cohortsSorted = [...cohortsItems].sort((a, b) => parsePatients(b.metric) - parsePatients(a.metric));

  // Providers hover data and sorting by wRVUs (descending)
  const providersItems = [
    { id: 'PR001', name: 'Elena Vargas, MD', specialty: 'Orthopedic Surgery', metric: "13,550 wRVU's, T12M" },
    { id: 'PR002', name: 'Marcus Chen, MD', specialty: 'Interventional Cardiology', metric: "11,200 wRVU's, T12M" },
    { id: 'PR003', name: 'Nicole Fisher, MD', specialty: 'Primary Care/Internal Medicine', metric: "6,350 wRVU's" },
    { id: 'PR004', name: 'Javier Morales, MD', specialty: 'Neurosurgery', metric: "16,800 wRVU's, T12M" },
  ];

  const parseWrvu = (metric: string): number => {
    const match = metric.match(/([0-9][0-9.,]*)\s*w?rvu/i);
    if (!match) return 0;
    // Remove all non-digits to normalize commas/period thousand separators
    const digits = match[1].replace(/[^0-9]/g, '');
    return parseInt(digits || '0', 10);
  };

  const providersSorted = [...providersItems].sort((a, b) => parseWrvu(b.metric) - parseWrvu(a.metric));

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 64px)', // account for app bar
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 2, md: 4 },
        py: { xs: 4, md: 6 }
      }}
    >
      {/* Orbital layout container */}
      <Box
        sx={{ position: 'relative', width: containerSize, height: containerSize }}
      >
        {/* Central circle */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: centralSize,
            height: centralSize,
            borderRadius: '50%',
            border: isDark ? '1px solid rgba(0,180,255,0.35)' : '1px solid rgba(0,180,255,0.25)',
            background: isDark 
              ? 'radial-gradient(ellipse at center, rgba(26,26,26,0.9), rgba(0,0,0,0.95))'
              : 'radial-gradient(ellipse at center, rgba(245,245,247,0.9), rgba(255,255,255,0.95))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            boxShadow: isDark ? '0 12px 35px rgba(138,43,226,0.25)' : '0 12px 35px rgba(138,43,226,0.15)'
          }}
        >
          <Box>
            {/* Generic Healthcare System logo placeholder */}
            <Box
              sx={{
                width: { xs: 36, sm: 42, md: 48 },
                height: { xs: 36, sm: 42, md: 48 },
                borderRadius: '50%',
                background: 'linear-gradient(90deg, #8A2BE2, #00B4FF)',
                mb: 1.5,
                mx: 'auto'
              }}
            />
            <Typography
              variant="subtitle1"
              sx={{ ...gradientText, fontWeight: 800, letterSpacing: 0.3 }}
            >
              Healthcare System
            </Typography>
          </Box>
        </Box>

        {/* Satellite circles evenly spaced around center */}
        {satellites.map((s, i) => {
          const angle = (i * 2 * Math.PI) / satellites.length - Math.PI / 2; // start at top
          const x = center + radius * Math.cos(angle);
          const y = center + radius * Math.sin(angle);
          return (
            <Box
              key={s.label}
              sx={{
                position: 'absolute',
                left: x,
                top: y,
                transform: 'translate(-50%, -50%)'
              }}
              onMouseEnter={() => handleHoverEnter(s.label)}
              onMouseLeave={handleHoverLeave}
            >
              <CircleButton
                label={s.label}
                onClick={s.onClick}
                badgeCount={s.label === 'Iris Alerts' ? 5 : undefined}
              />

              {/* Hover panel for Iris Care Score with elbow connector */}
              {s.label === 'Iris Care Score' && hovered === 'Iris Care Score' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10,
                    pointerEvents: 'auto'
                  }}
                  onMouseEnter={() => handleHoverEnter(s.label)}
                  onMouseLeave={handleHoverLeave}
                >
                  {(() => {
                    const startOffset = satelliteSize / 2;
                    const hLen = isMdUp ? 240 : isSmUp ? 200 : 180; // Extended horizontal line to push modal beyond Top Facilities circle
                    const vLen = isMdUp ? 110 : isSmUp ? 95 : 80;
                    const dirH = x >= center ? 1 : -1;
                    const dirV = y >= center ? 1 : -1;
                    const horizGradient = dirH > 0
                      ? 'linear-gradient(90deg, rgba(138,43,226,0.0), rgba(138,43,226,0.9))'
                      : 'linear-gradient(270deg, rgba(138,43,226,0.0), rgba(138,43,226,0.9))';
                    const vertGradient = dirV > 0
                      ? 'linear-gradient(0deg, rgba(138,43,226,0.0), rgba(138,43,226,0.9))'
                      : 'linear-gradient(180deg, rgba(138,43,226,0.9), rgba(138,43,226,0.0))';
                    return (
                      <>
                        <Box sx={{ '@keyframes hGrowCS': { from: { width: 0 }, to: { width: `${hLen}px` } }, '@keyframes vGrowCS': { from: { height: 0 }, to: { height: `${vLen}px` } } }} />
                        <Box sx={{ position: 'absolute', top: 0, ...(dirH > 0 ? { left: startOffset } : { right: startOffset }), height: '2px', width: 0, background: horizGradient, boxShadow: '0 0 8px rgba(138,43,226,0.5)', animation: 'hGrowCS 200ms ease forwards' }} />
                        <Box sx={{ position: 'absolute', ...(dirV > 0 ? { top: 0 } : { top: -vLen }), ...(dirH > 0 ? { left: startOffset + hLen } : { right: startOffset + hLen }), width: '2px', height: 0, background: vertGradient, boxShadow: '0 0 8px rgba(138,43,226,0.5)', animation: 'vGrowCS 220ms 180ms ease forwards' }} />
                        <Paper elevation={6} sx={{ position: 'absolute', ...(dirV > 0 ? { top: vLen + 8 } : { top: -vLen - 8 }), ...(dirH > 0 ? { left: startOffset + hLen + 8 } : { right: startOffset + hLen + 8 }), p: 2, minWidth: { xs: 240, sm: 280 }, borderRadius: 2, border: isDark ? '1px solid rgba(138,43,226,0.25)' : '1px solid rgba(138,43,226,0.15)', background: isDark ? 'linear-gradient(180deg, rgba(11,11,11,0.96), rgba(0,0,0,0.96))' : 'linear-gradient(180deg, rgba(255,255,255,0.96), rgba(245,245,247,0.96))', boxShadow: isDark ? '0 12px 35px rgba(138,43,226,0.25)' : '0 12px 35px rgba(138,43,226,0.15)', overflow: 'hidden', color: theme.palette.text.primary }}>
                          <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#8A2BE2' }} />
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', background: 'linear-gradient(90deg, #8A2BE2, #00B4FF)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Iris Care Score</Typography>
                          </Box>
                          <Box sx={{ '@keyframes scoreIn': { from: { transform: 'translateY(8px)', opacity: 0 }, to: { transform: 'translateY(0)', opacity: 1 } } }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 1, borderBottom: isDark ? '1px dashed rgba(138,43,226,0.25)' : '1px dashed rgba(138,43,226,0.15)', animation: 'scoreIn 260ms ease forwards', animationDelay: '0ms', opacity: 0 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: 'linear-gradient(90deg, #8A2BE2, #00B4FF)' }} />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.15, color: theme.palette.text.primary }}>Organization Care Score: 78</Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 1, borderBottom: isDark ? '1px dashed rgba(138,43,226,0.25)' : '1px dashed rgba(138,43,226,0.15)', animation: 'scoreIn 260ms ease forwards', animationDelay: '80ms', opacity: 0 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: 'linear-gradient(90deg, #8A2BE2, #00B4FF)' }} />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.15, color: theme.palette.text.primary }}>Top Facility: Iris Memorial Hospital</Typography>
                                <Typography sx={{ fontSize: '0.8rem', color: theme.palette.primary.main, opacity: 0.9, mt: 0.5 }}>Care Score: 83</Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 1, animation: 'scoreIn 260ms ease forwards', animationDelay: '160ms', opacity: 0 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: 'linear-gradient(90deg, #8A2BE2, #00B4FF)' }} />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.15, color: theme.palette.text.primary }}>Top Provider: Dr. Jane Smith</Typography>
                                <Typography sx={{ fontSize: '0.8rem', color: theme.palette.primary.main, opacity: 0.9, mt: 0.5 }}>Care Score: 91</Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Paper>
                      </>
                    );
                  })()}
                </Box>
              )}

              {/* Hover panel for Top Facilities with elbow connector */}
              {s.label === 'Top Facilities' && hovered === 'Top Facilities' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10,
                    pointerEvents: 'auto'
                  }}
                  onMouseEnter={() => handleHoverEnter(s.label)}
                  onMouseLeave={handleHoverLeave}
                >
                  {/* Compute connector geometry based on quadrant */}
                  {(() => {
                    const startOffset = satelliteSize / 2; // starting from circle edge
                    const hLen = isMdUp ? 140 : isSmUp ? 120 : 100;
                    const vLen = isMdUp ? 110 : isSmUp ? 95 : 80;
                    // Determine quadrant based on circle position relative to center
                    const dirH = x >= center ? 1 : -1; // 1 = right, -1 = left
                    const dirV = y >= center ? 1 : -1; // 1 = down, -1 = up
                    const horizGradient = dirH > 0
                      ? 'linear-gradient(90deg, rgba(0,180,255,0.0), rgba(0,180,255,0.9))'
                      : 'linear-gradient(270deg, rgba(0,180,255,0.0), rgba(0,180,255,0.9))';
                    const vertGradient = dirV > 0
                      ? 'linear-gradient(0deg, rgba(0,180,255,0.0), rgba(0,180,255,0.9))'
                      : 'linear-gradient(180deg, rgba(0,180,255,0.9), rgba(0,180,255,0.0))';

                    return (
                      <>
                        {/* keyframes */}
                        <Box sx={{
                          '@keyframes hGrow': { from: { width: 0 }, to: { width: `${hLen}px` } },
                          '@keyframes vGrow': { from: { height: 0 }, to: { height: `${vLen}px` } }
                        }} />
                        {/* horizontal segment (left or right) */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            ...(dirH > 0 ? { left: startOffset } : { right: startOffset }),
                            height: '2px',
                            width: 0,
                            background: horizGradient,
                            boxShadow: '0 0 8px rgba(0,180,255,0.5)',
                            animation: 'hGrow 200ms ease forwards'
                          }}
                        />
                        {/* vertical segment (up or down) anchored at end of horizontal */}
                        <Box
                          sx={{
                            position: 'absolute',
                            ...(dirV > 0 ? { top: 0 } : { top: -vLen }),
                            ...(dirH > 0 ? { left: startOffset + hLen } : { right: startOffset + hLen }),
                            width: '2px',
                            height: 0,
                            background: vertGradient,
                            boxShadow: '0 0 8px rgba(0,180,255,0.5)',
                            animation: 'vGrow 220ms 180ms ease forwards'
                          }}
                        />
                        {/* popup anchored to elbow tip */}
                        <Paper
                          elevation={6}
                          sx={{
                            position: 'absolute',
                            ...(dirV > 0 ? { top: vLen + 8 } : { top: -vLen - 8 }),
                            ...(dirH > 0 ? { left: startOffset + hLen + 8 } : { right: startOffset + hLen + 8 }),
                            p: 2,
                            minWidth: { xs: 220, sm: 260 },
                            borderRadius: 2,
                            border: isDark ? '1px solid rgba(0,180,255,0.25)' : '1px solid rgba(0,180,255,0.15)',
                            background: isDark 
                              ? 'linear-gradient(180deg, rgba(11,11,11,0.96), rgba(0,0,0,0.96))'
                              : 'linear-gradient(180deg, rgba(255,255,255,0.96), rgba(245,245,247,0.96))',
                            boxShadow: isDark ? '0 12px 35px rgba(0,180,255,0.25)' : '0 12px 35px rgba(0,180,255,0.15)',
                            color: theme.palette.text.primary,
                            overflow: 'hidden'
                          }}
                        >
                          {/* Retro/arcade-style header bar */}
                          <Box
                            sx={{
                              mb: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#00B4FF' }} />
                            <Typography sx={{
                              fontSize: '0.8rem',
                              fontWeight: 800,
                              letterSpacing: 1,
                              textTransform: 'uppercase',
                              background: 'linear-gradient(90deg, #8A2BE2, #00B4FF)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent'
                            }}>
                              Top Facilities
                            </Typography>
                          </Box>

                          {/* Keyframes for list animation */}
                          <Box sx={{
                            '@keyframes arsenalIn': {
                              from: { transform: 'translateX(16px)', opacity: 0 },
                              to: { transform: 'translateX(0)', opacity: 1 }
                            }
                          }}>
                            {facilitiesSorted.map((item, idx) => (
                              <Box
                                key={item.id}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.25,
                                  py: 1,
                                  borderBottom: idx !== facilitiesSorted.length - 1 ? (isDark ? '1px dashed rgba(138,43,226,0.25)' : '1px dashed rgba(138,43,226,0.15)') : 'none',
                                  animation: 'arsenalIn 300ms ease forwards',
                                  animationDelay: `${idx * 90}ms`,
                                  opacity: 0
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    background: 'linear-gradient(90deg, #8A2BE2, #00B4FF)'
                                  }}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    noWrap
                                    sx={{
                                    fontWeight: 700,
                                    fontSize: '0.95rem',
                                    lineHeight: 1.15,
                                    color: theme.palette.text.primary
                                  }}
                                >
                                    {item.id} - {item.name}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: '0.8rem',
                                      color: theme.palette.primary.main,
                                      opacity: 0.9
                                    }}
                                  >
                                    {item.metric}
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Paper>
                      </>
                    );
                  })()}
                </Box>
              )}

              {/* Hover panel for Top Patient Cohorts with elbow connector */}
              {s.label === 'Top Patient Cohorts' && hovered === 'Top Patient Cohorts' && (
                <Box
                  sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10, pointerEvents: 'auto' }}
                  onMouseEnter={() => handleHoverEnter(s.label)}
                  onMouseLeave={handleHoverLeave}
                >
                  {(() => {
                    const startOffset = satelliteSize / 2;
                    const hLen = isMdUp ? 140 : isSmUp ? 120 : 100;
                    const vLen = isMdUp ? 110 : isSmUp ? 95 : 80;
                    const dirH = x >= center ? 1 : -1;
                    // Force popup to open upward to avoid page scroll on bottom positions
                    const dirV = -1;
                    const horizGradient = dirH > 0
                      ? 'linear-gradient(90deg, rgba(138,43,226,0.0), rgba(138,43,226,0.9))'
                      : 'linear-gradient(270deg, rgba(138,43,226,0.0), rgba(138,43,226,0.9))';
                    const vertGradient = dirV > 0
                      ? 'linear-gradient(0deg, rgba(138,43,226,0.0), rgba(138,43,226,0.9))'
                      : 'linear-gradient(180deg, rgba(138,43,226,0.9), rgba(138,43,226,0.0))';
                    return (
                      <>
                        <Box sx={{ '@keyframes hGrowC': { from: { width: 0 }, to: { width: `${hLen}px` } }, '@keyframes vGrowC': { from: { height: 0 }, to: { height: `${vLen}px` } } }} />
                        <Box sx={{ position: 'absolute', top: 0, ...(dirH > 0 ? { left: startOffset } : { right: startOffset }), height: '2px', width: 0, background: horizGradient, boxShadow: '0 0 8px rgba(138,43,226,0.5)', animation: 'hGrowC 200ms ease forwards' }} />
                        <Box sx={{ position: 'absolute', ...(dirV > 0 ? { top: 0 } : { top: -vLen }), ...(dirH > 0 ? { left: startOffset + hLen } : { right: startOffset + hLen }), width: '2px', height: 0, background: vertGradient, boxShadow: '0 0 8px rgba(138,43,226,0.5)', animation: 'vGrowC 220ms 180ms ease forwards' }} />
                        <Paper elevation={6} sx={{ position: 'absolute', ...(dirV > 0 ? { top: vLen + 8 } : { top: -vLen - 8 }), ...(dirH > 0 ? { left: startOffset + hLen + 8 } : { right: startOffset + hLen + 8 }), p: 2, minWidth: { xs: 220, sm: 260 }, maxHeight: '60vh', overflowY: 'auto', borderRadius: 2, border: isDark ? '1px solid rgba(138,43,226,0.25)' : '1px solid rgba(138,43,226,0.15)', background: isDark ? 'linear-gradient(180deg, rgba(11,11,11,0.96), rgba(0,0,0,0.96))' : 'linear-gradient(180deg, rgba(255,255,255,0.96), rgba(245,245,247,0.96))', boxShadow: isDark ? '0 12px 35px rgba(138,43,226,0.25)' : '0 12px 35px rgba(138,43,226,0.15)', overflow: 'hidden', color: theme.palette.text.primary }}>
                          <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#8A2BE2' }} />
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', background: 'linear-gradient(90deg, #8A2BE2, #00B4FF)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Top Patient Cohorts</Typography>
                          </Box>
                          <Box sx={{ '@keyframes arsenalIn': { from: { transform: 'translateX(-16px)', opacity: 0 }, to: { transform: 'translateX(0)', opacity: 1 } } }}>
                            {cohortsSorted.map((item, idx) => (
                              <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1, borderBottom: idx !== cohortsSorted.length - 1 ? (isDark ? '1px dashed rgba(138,43,226,0.25)' : '1px dashed rgba(138,43,226,0.15)') : 'none', animation: 'arsenalIn 300ms ease forwards', animationDelay: `${idx * 90}ms`, opacity: 0 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: 'linear-gradient(90deg, #8A2BE2, #00B4FF)' }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.15, color: theme.palette.text.primary }}>{item.name}</Typography>
                                  <Typography sx={{ fontSize: '0.8rem', color: theme.palette.primary.main, opacity: 0.9 }}>{item.metric}</Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Paper>
                      </>
                    );
                  })()}
                </Box>
              )}

              {/* Hover panel for Top Providers with elbow connector */}
              {s.label === 'Top Providers' && hovered === 'Top Providers' && (
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10, pointerEvents: 'auto' }}
                  onMouseEnter={() => handleHoverEnter(s.label)}
                  onMouseLeave={handleHoverLeave}
                >
                  {(() => {
                    const startOffset = satelliteSize / 2;
                    const hLen = isMdUp ? 140 : isSmUp ? 120 : 100;
                    const vLen = isMdUp ? 110 : isSmUp ? 95 : 80;
                    const dirH = x >= center ? 1 : -1;
                    // Force popup to open upward to avoid page scroll on bottom positions
                    const dirV = -1;
                    const horizGradient = dirH > 0
                      ? 'linear-gradient(90deg, rgba(0,180,255,0.0), rgba(0,180,255,0.9))'
                      : 'linear-gradient(270deg, rgba(0,180,255,0.0), rgba(0,180,255,0.9))';
                    const vertGradient = dirV > 0
                      ? 'linear-gradient(0deg, rgba(0,180,255,0.0), rgba(0,180,255,0.9))'
                      : 'linear-gradient(180deg, rgba(0,180,255,0.9), rgba(0,180,255,0.0))';
                    return (
                      <>
                        <Box sx={{ '@keyframes hGrowP': { from: { width: 0 }, to: { width: `${hLen}px` } }, '@keyframes vGrowP': { from: { height: 0 }, to: { height: `${vLen}px` } } }} />
                        <Box sx={{ position: 'absolute', top: 0, ...(dirH > 0 ? { left: startOffset } : { right: startOffset }), height: '2px', width: 0, background: horizGradient, boxShadow: '0 0 8px rgba(0,180,255,0.5)', animation: 'hGrowP 200ms ease forwards' }} />
                        <Box sx={{ position: 'absolute', ...(dirV > 0 ? { top: 0 } : { top: -vLen }), ...(dirH > 0 ? { left: startOffset + hLen } : { right: startOffset + hLen }), width: '2px', height: 0, background: vertGradient, boxShadow: '0 0 8px rgba(0,180,255,0.5)', animation: 'vGrowP 220ms 180ms ease forwards' }} />
                        <Paper elevation={6} sx={{ position: 'absolute', ...(dirV > 0 ? { top: vLen + 8 } : { top: -vLen - 8 }), ...(dirH > 0 ? { left: startOffset + hLen + 8 } : { right: startOffset + hLen + 8 }), p: 2, minWidth: { xs: 240, sm: 280 }, maxHeight: '60vh', overflowY: 'auto', borderRadius: 2, border: isDark ? '1px solid rgba(0,180,255,0.25)' : '1px solid rgba(0,180,255,0.15)', background: isDark ? 'linear-gradient(180deg, rgba(11,11,11,0.96), rgba(0,0,0,0.96))' : 'linear-gradient(180deg, rgba(255,255,255,0.96), rgba(245,245,247,0.96))', boxShadow: isDark ? '0 12px 35px rgba(0,180,255,0.25)' : '0 12px 35px rgba(0,180,255,0.15)', overflow: 'hidden', color: theme.palette.text.primary }}>
                          <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#00B4FF' }} />
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', background: 'linear-gradient(90deg, #8A2BE2, #00B4FF)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Top Providers</Typography>
                          </Box>
                          <Box sx={{ '@keyframes arsenalIn': { from: { transform: 'translateY(8px)', opacity: 0 }, to: { transform: 'translateY(0)', opacity: 1 } } }}>
                            {providersSorted.map((p, idx) => (
                              <Box key={p.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 1, borderBottom: idx !== providersSorted.length - 1 ? (isDark ? '1px dashed rgba(138,43,226,0.25)' : '1px dashed rgba(138,43,226,0.15)') : 'none', animation: 'arsenalIn 260ms ease forwards', animationDelay: `${idx * 80}ms`, opacity: 0 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: 'linear-gradient(90deg, #8A2BE2, #00B4FF)' }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.15, color: theme.palette.text.primary }}>{p.name} ({p.specialty})</Typography>
                                  <Typography sx={{ fontSize: '0.8rem', color: theme.palette.primary.main, opacity: 0.9 }}>{p.metric}</Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Paper>
                      </>
                    );
                  })()}
                </Box>
              )}

              {/* Hover panel for Iris Alerts with elbow connector */}
              {s.label === 'Iris Alerts' && hovered === 'Iris Alerts' && (
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10, pointerEvents: 'auto' }}
                  onMouseEnter={() => handleHoverEnter(s.label)}
                  onMouseLeave={handleHoverLeave}
                >
                  {(() => {
                    const startOffset = satelliteSize / 2;
                    const hLen = isMdUp ? 140 : isSmUp ? 120 : 100;
                    const vLen = isMdUp ? 110 : isSmUp ? 95 : 80;
                    const dirH = x >= center ? 1 : -1;
                    const dirV = y >= center ? 1 : -1;
                    const horizGradient = dirH > 0
                      ? 'linear-gradient(90deg, rgba(255,23,68,0.0), rgba(255,23,68,0.9))'
                      : 'linear-gradient(270deg, rgba(255,23,68,0.0), rgba(255,23,68,0.9))';
                    const vertGradient = dirV > 0
                      ? 'linear-gradient(0deg, rgba(255,23,68,0.0), rgba(255,23,68,0.9))'
                      : 'linear-gradient(180deg, rgba(255,23,68,0.9), rgba(255,23,68,0.0))';
                    return (
                      <>
                        <Box sx={{ '@keyframes hGrowA': { from: { width: 0 }, to: { width: `${hLen}px` } }, '@keyframes vGrowA': { from: { height: 0 }, to: { height: `${vLen}px` } } }} />
                        <Box sx={{ position: 'absolute', top: 0, ...(dirH > 0 ? { left: startOffset } : { right: startOffset }), height: '2px', width: 0, background: horizGradient, boxShadow: '0 0 8px rgba(255,23,68,0.5)', animation: 'hGrowA 200ms ease forwards' }} />
                        <Box sx={{ position: 'absolute', ...(dirV > 0 ? { top: 0 } : { top: -vLen }), ...(dirH > 0 ? { left: startOffset + hLen } : { right: startOffset + hLen }), width: '2px', height: 0, background: vertGradient, boxShadow: '0 0 8px rgba(255,23,68,0.5)', animation: 'vGrowA 220ms 180ms ease forwards' }} />
                        <Paper elevation={6} sx={{ position: 'absolute', ...(dirV > 0 ? { top: vLen + 8 } : { top: -vLen - 8 }), ...(dirH > 0 ? { left: startOffset + hLen + 8 } : { right: startOffset + hLen + 8 }), p: 2, minWidth: { xs: 260, sm: 300 }, borderRadius: 2, border: '1px solid rgba(255,0,0,0.35)', background: isDark ? 'linear-gradient(180deg, rgba(20,0,0,0.96), rgba(0,0,0,0.96))' : 'linear-gradient(180deg, rgba(255,245,245,0.96), rgba(255,255,255,0.96))', boxShadow: '0 12px 35px rgba(255,0,0,0.35)', overflow: 'hidden', color: theme.palette.text.primary }}>
                          <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#ff1744' }} />
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', background: 'linear-gradient(90deg, #ff1744, #ff9100)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Alerts (5)</Typography>
                          </Box>
                          <Box sx={{ '@keyframes alertIn': { from: { transform: 'translateY(-8px)', opacity: 0 }, to: { transform: 'translateY(0)', opacity: 1 } } }}>
                            {[
                              { text: 'Patient Leakage: New Competitor ASC Expansion Plans Detected in Miami-Dade', searchQuery: 'Miami-Dade', clickable: true },
                              { text: 'High-Risk Cohort Travel Barrier Detected', searchQuery: null, clickable: false },
                              { text: "Predicted Competitor M&A: FL125 Threat", searchQuery: null, clickable: false },
                              { text: '30-Day Readmission Spike: Facility FL124', searchQuery: null, clickable: false },
                              { text: 'New Market Opportunity: Unmet Cardiology Demand Hotspot', searchQuery: null, clickable: false }
                            ].map((alert, idx, arr) => (
                              <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 1, borderBottom: idx !== arr.length - 1 ? '1px dashed rgba(255,23,68,0.35)' : 'none', animation: 'alertIn 260ms ease forwards', animationDelay: `${idx * 80}ms`, opacity: 0 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: 'linear-gradient(90deg, #ff1744, #ff9100)' }} />
                                <Typography
                                  sx={{
                                    fontSize: '0.9rem',
                                    lineHeight: 1.2,
                                    color: theme.palette.text.primary,
                                    pr: 1,
                                    ...(alert.clickable ? {
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                      textDecorationColor: 'rgba(255,23,68,0.5)',
                                      '&:hover': {
                                        color: '#ff9100',
                                        textDecorationColor: '#ff9100'
                                      }
                                    } : {})
                                  }}
                                  onClick={alert.clickable ? () => {
                                    setHovered(null);
                                    navigate(`/3dmap?search=${encodeURIComponent(alert.searchQuery!)}&alert=patient-leakage`);
                                  } : undefined}
                                >
                                  {alert.text}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Paper>
                      </>
                    );
                  })()}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default Dashboard;
